import { Add20Regular } from '@fluentui/react-icons';
import { Button } from '../../../../Button';
import { Flex } from '../../../../Flex';
import { TimeSlot } from '@mnspro-cloud/services';
import { isEqual, orderBy } from 'lodash';
import moment from 'moment-timezone';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TimeMapping } from '../TimeMapping/TimeMapping';

export interface DayWithMappingsProps {
  mappings: TimeSlot[];
  originalMappings?: TimeSlot[];
  displayMode?: boolean;
  addMapping?: (newItem: TimeSlot) => void;
  updateMapping?: (updatedItem: TimeSlot, index: number) => void;
  deleteButtonClicked?: (item: TimeSlot) => void;
  validityChanged?: (isValid: boolean) => void;
  creationModeChanged?: (isInCreationMode: boolean) => void;
}

export const DayWithMappings: FC<DayWithMappingsProps> = props => {
  const { t } = useTranslation();
  const {
    mappings,
    originalMappings,
    displayMode,
    addMapping,
    updateMapping,
    deleteButtonClicked,
    validityChanged,
    creationModeChanged,
  } = props;

  const orderedMappings = useMemo(
    () => orderBy(mappings, mapping => mapping.start),
    [mappings],
  );

  const isNewMapping = (mapping: TimeSlot): boolean => {
    if (!originalMappings) return false;
    return !originalMappings.some(original => isEqual(original, mapping));
  };
  const [creationMode, setCreationMode] = useState(false);

  useEffect(() => {
    creationModeChanged?.(creationMode);
  }, [creationMode]);

  const handleCreation = (createdItem: TimeSlot) => {
    addMapping?.(createdItem);
    setCreationMode(false);
  };

  const handleCancelCreation = () => {
    setCreationMode(false);
    validityChanged?.(true);
  };

  const fromInitialValue =
    orderedMappings.length > 0
      ? orderedMappings[orderedMappings.length - 1]?.end
      : '08:00';

  return (
    <Flex direction="column" align="flex-start" gap="0.5rem" fill id="outerDayWithMappings">
      {orderedMappings.map((timeMapping, index) => (
        <TimeMapping
          key={`${index}_${timeMapping.name}`}
          start={timeMapping.start}
          end={timeMapping.end}
          name={timeMapping.name}
          isBreak={timeMapping.isBreak}
          canChangeBreakStatus={isNewMapping(timeMapping)}
          updateItem={updatedItem => updateMapping?.(updatedItem, index)}
          deleteClicked={() => deleteButtonClicked?.(timeMapping)}
          validityChanged={validityChanged}
          displayMode={displayMode}
        />
      ))}
      {creationMode && (
        <TimeMapping
          startInEditMode
          canChangeBreakStatus
          start={fromInitialValue}
          end={moment(`1970-01-01T${fromInitialValue}`)
            .add(45, 'minutes')
            .format('HH:mm')}
          name=""
          isBreak={false}
          updateItem={handleCreation}
          cancelButtonClicked={handleCancelCreation}
          validityChanged={validityChanged}
          displayMode={displayMode}
        />
      )}
      {!displayMode && (
        <Button
          data-testid="dayMappingAddButton"
          appearance="transparent"
          icon={<Add20Regular />}
          style={{ marginTop: '1rem', marginBottom: '2rem' }}
          onClick={() => setCreationMode(true)}
          disabled={creationMode}
        >
          {t('settings_timepattern_add_hour_btn_label', { defaultValue: 'Schulstunde hinzufügen' })}
        </Button>
      )}
    </Flex>
  );
};
